import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { 
  CheckCircle2, 
  MessageSquare, 
  FileCheck, 
  Users, 
  Shield,
  ChevronDown,
  Phone,
  Mail,
  MapPin
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "./ui/carousel";
import autoplay from "embla-carousel-autoplay";

const LandingPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  
  const plugin = React.useRef(
    autoplay({ delay: 4000, stopOnInteraction: true })
  );

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Load GoHighLevel form scripts
  useEffect(() => {
    // Script for general form
    const generalScript = document.createElement('script');
    generalScript.src = 'https://links.thefunnelbox.com/js/form_embed.js';
    generalScript.async = true;
    document.body.appendChild(generalScript);

    return () => {
      if (document.body.contains(generalScript)) {
        document.body.removeChild(generalScript);
      }
    };
  }, []);

  const scrollToContact = () => {
    const contactSection = document.getElementById('contacto');
    contactSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleWhatsApp = () => {
    setShowWhatsAppModal(true);
  };

  return (
    <div className="landing-page">
      {/* Header Navigation */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#0a1628]/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}>
        <nav className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_google-ads-landing-1/artifacts/wfsf087n_image.png" 
                alt="InterlegalP" 
                className="h-12 w-auto"
              />
            </div>
            <div className="hidden md:flex items-center space-x-6">
              <button onClick={() => scrollToSection('inicio')} className="nav-link">Inicio</button>
              <button onClick={() => scrollToSection('beneficios')} className="nav-link">Beneficios</button>
              <button onClick={() => scrollToSection('proceso')} className="nav-link">Proceso</button>
              <button onClick={() => scrollToSection('nosotros')} className="nav-link">Nosotros</button>
              <button onClick={() => scrollToSection('faq')} className="nav-link">FAQ</button>
              <Button onClick={scrollToContact} className="cta-button">
                Solicitar Consulta
              </Button>
            </div>
          </div>
        </nav>
      </header>

      {/* WhatsApp Floating Button */}
      <button 
        onClick={handleWhatsApp}
        className="whatsapp-float"
        aria-label="Contactar por WhatsApp"
      >
        <svg className="h-7 w-7" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
      </button>

      {/* WhatsApp Modal */}
      <Dialog open={showWhatsAppModal} onOpenChange={setShowWhatsAppModal}>
        <DialogContent className="whatsapp-modal-wrapper">
          <div className="whatsapp-modal-green-header">
            <h2 className="whatsapp-modal-title-text">Contactar por WhatsApp</h2>
            <button 
              onClick={() => setShowWhatsAppModal(false)}
              className="whatsapp-close-btn"
              aria-label="Cerrar"
            >
              ✕
            </button>
          </div>
          <div className="whatsapp-iframe-container">
            <iframe
              src="https://links.thefunnelbox.com/widget/form/U8VYeC56Qr5PRs8aRhH5"
              style={{width:'100%', height:'828px', border:'none', borderRadius:'0'}}
              id="inline-U8VYeC56Qr5PRs8aRhH5" 
              data-layout="{'id':'INLINE'}"
              data-trigger-type="alwaysShow"
              data-trigger-value=""
              data-activation-type="alwaysActivated"
              data-activation-value=""
              data-deactivation-type="neverDeactivate"
              data-deactivation-value=""
              data-form-name="FORM_GOOGLE_ADS_VISA_WHATSAPP"
              data-height="828"
              data-layout-iframe-id="inline-U8VYeC56Qr5PRs8aRhH5"
              data-form-id="U8VYeC56Qr5PRs8aRhH5"
              title="FORM_GOOGLE_ADS_VISA_WHATSAPP"
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Hero Section */}
      <section id="inicio" className="hero-section">
        <div className="hero-overlay"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            {/* Left Column - Content */}
            <div className="text-left">
              <div className="hero-badge">
                No somos gestores, somos un despacho jurídico
              </div>
              <h1 className="hero-title">
                ¿Desea tramitar una visa para Estados Unidos con respaldo jurídico especializado?
              </h1>
              <p className="hero-subtitle">
                En InterlegalP asesoramos a personas, empresarios y profesionales que buscan estructurar correctamente su proceso migratorio hacia Estados Unidos.
              </p>
              <p className="hero-description">
                Analizamos su perfil y diseñamos la estrategia legal adecuada para su caso.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-3 my-6">
                {[
                  'Quiere tramitar una visa de inversión o negocios',
                  'Busca establecerse profesionalmente en Estados Unidos',
                  'Necesita una visa de trabajo, estudiante o turismo',
                  'Requiere asesoría jurídica para evitar errores en su solicitud'
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-3 feature-item">
                    <CheckCircle2 className="h-5 w-5 text-[#d4af37] flex-shrink-0 mt-1" />
                    <span className="text-white/90 text-sm">{item}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 mt-6">
                <Button onClick={scrollToContact} size="lg" className="cta-button-primary">
                  Solicitar Consulta
                </Button>
                <Button onClick={handleWhatsApp} size="lg" className="cta-button-whatsapp">
                  <svg className="mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  WhatsApp
                </Button>
              </div>
            </div>

            {/* Right Column - Image */}
            <div className="hidden lg:block">
              <div className="hero-image-container">
                <img 
                  src="https://images.unsplash.com/photo-1740597960775-edabd0f6d22a?w=800&q=80" 
                  alt="Éxito inmigratorio en Estados Unidos" 
                  className="hero-image"
                />
                <div className="hero-image-overlay"></div>
              </div>
            </div>
          </div>

          <div className="text-center mt-8 animate-bounce">
            <ChevronDown className="h-8 w-8 text-[#d4af37] mx-auto" />
          </div>
        </div>
      </section>

      {/* Beneficios Section */}
      <section id="beneficios" className="section-light">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center mb-16">
            <div className="section-badge-blue">Beneficios</div>
            <h2 className="section-title max-w-4xl mx-auto">
              <span className="block">¿Le resulta familiar esta situación?</span>
              <span className="block mt-2">Nosotros la estructuramos correctamente.</span>
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-center mb-16 max-w-6xl mx-auto">
            <div className="grid sm:grid-cols-2 gap-6">
              {[
                'No sabe qué tipo de visa se adapta mejor a su perfil',
                'Tiene dudas sobre la viabilidad real de su solicitud',
                'Quiere evitar errores que puedan afectar futuras aplicaciones',
                'Busca asesoría profesional antes de iniciar el proceso'
              ].map((problema, index) => (
                <Card key={index} className="problem-card">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-3">
                      <div className="h-2 w-2 bg-[#d4af37] rounded-full mt-2"></div>
                      <p className="text-gray-700 font-medium">{problema}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img 
                src="https://images.unsplash.com/photo-1573164574572-cb89e39749b4?w=700&q=80" 
                alt="Reunión profesional" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="max-w-4xl mx-auto">
            <h3 className="text-2xl md:text-3xl font-bold text-center mb-8 text-[#0a1628]">
              ¿Qué obtiene al trabajar con InterlegalP?
            </h3>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {[
                'Nuestras oficinas están en San Antonio, Texas. Trabajamos desde Estados Unidos',
                'Evaluación estratégica personalizada de su perfil migratorio',
                'Definición clara de la visa adecuada según su objetivo',
                'Preparación estructurada del expediente',
                'Acompañamiento jurídico durante el proceso',
                'Orientación para minimizar riesgos y rechazos'
              ].map((beneficio, index) => (
                <div key={index} className="benefit-item">
                  <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">{beneficio}</span>
                </div>
              ))}
            </div>

            <Card className="result-card">
              <CardContent className="p-8">
                <h4 className="text-xl font-bold mb-4 text-[#0a1628]">Beneficio real para usted</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    'Mayor claridad antes de iniciar el trámite',
                    'Reducción de errores comunes en la solicitud',
                    'Seguridad jurídica en la estructuración del caso',
                    'Confianza en cada etapa del proceso migratorio'
                  ].map((resultado, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <div className="h-1.5 w-1.5 bg-[#d4af37] rounded-full"></div>
                      <span className="text-gray-700">{resultado}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <div className="text-center mt-8">
              <Button onClick={scrollToContact} className="cta-button">
                Solicitar Consulta
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Proceso Section */}
      <section id="proceso" className="section-dark">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="section-badge-light">El Proceso</div>
            <h2 className="section-title-light">
              Un proceso estructurado para maximizar la viabilidad de su visa
            </h2>
            <p className="section-subtitle-light mt-6">
              En InterlegalP trabajamos con metodología jurídica clara. Antes de iniciar cualquier trámite, evaluamos la estrategia adecuada según su perfil.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12 max-w-7xl mx-auto">
            {[
              {
                number: '1',
                title: 'Evaluación inicial del perfil',
                description: 'Analizamos su situación profesional, financiera o académica para determinar viabilidad.',
                icon: FileCheck
              },
              {
                number: '2',
                title: 'Definición de estrategia migratoria',
                description: 'Identificamos el tipo de visa más adecuado según sus objetivos (inversión, trabajo, estudio o turismo).',
                icon: Shield
              },
              {
                number: '3',
                title: 'Preparación estructurada del expediente',
                description: 'Organizamos documentación, soportes y argumentación jurídica conforme a los requisitos migratorios.',
                icon: FileCheck
              },
              {
                number: '4',
                title: 'Acompañamiento durante el proceso',
                description: 'Brindamos seguimiento y orientación hasta la resolución de su solicitud.',
                icon: Users
              }
            ].map((step, index) => (
              <Card key={index} className="process-card">
                <CardContent className="p-6">
                  <div className="process-number">{step.number}</div>
                  <step.icon className="h-8 w-8 text-[#d4af37] mb-4" />
                  <h4 className="text-lg font-bold text-white mb-3">{step.title}</h4>
                  <p className="text-gray-300 text-sm">{step.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Button onClick={scrollToContact} className="cta-button-light">
              Solicitar Consulta
            </Button>
          </div>
        </div>
      </section>

      {/* Nosotros Section */}
      <section id="nosotros" className="section-light">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <div className="section-badge-blue">Nosotros</div>
              <h2 className="section-title mb-8">
                ABOGADOS DE INMIGRACIÓN DE VISAS: GARANTIZANDO TU ÉXITO EN INTER LEGAL
              </h2>
              <p className="section-subtitle max-w-4xl mx-auto">
                Encuentra a los mejores abogados de inmigración de <strong>visas E1, E2, EB-5 y TN</strong> en InterLegalP y asegura un proceso fluido y exitoso para obtener tu visa. Nuestro equipo de expertos legales cuenta con una amplia experiencia en leyes migratorias y está comprometido en brindarte asesoramiento personalizado y estratégico para maximizar tus posibilidades de éxito. Ya sea que necesites una <strong>visa de trabajo e inversión</strong>, nuestros <strong>abogados especializados</strong> te guiarán a través de cada etapa del proceso, asegurando que cumplas con todos los <strong>requisitos legales</strong>. Deja tu futuro en nuestras manos y confía en <strong>InterLegalP</strong> y nuestros abogados de inmigración de visas para alcanzar tus metas migratorias. ¡Contáctanos ahora mismo y comienza tu viaje hacia una nueva vida!
              </p>
            </div>

            {/* Team Photos */}
            <div className="grid md:grid-cols-2 gap-12 mb-16 max-w-5xl mx-auto">
              <div className="team-member-card-new">
                <div className="team-member-photo">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_google-ads-landing-1/artifacts/m6v0femj_Captura%20de%20pantalla%202026-03-04%20a%20las%2016.34.54.png" 
                    alt="Miguel Antonio Saucedo Ramírez" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="team-member-simple-badge">
                  MIGUEL ANTONIO SAUCEDO RAMÍREZ
                </div>
              </div>
              
              <div className="team-member-card-new">
                <div className="team-member-photo">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_google-ads-landing-1/artifacts/6iqybneh_Captura%20de%20pantalla%202026-03-04%20a%20las%2016.34.58.png" 
                    alt="Aldo Salazar" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="team-member-simple-badge">
                  ALDO SALAZAR
                </div>
              </div>
            </div>

            <div className="mb-16">
              <h3 className="text-3xl font-bold mb-12 text-center text-[#0a1628]">Lo que dicen nuestros clientes</h3>
              
              <Carousel
                plugins={[plugin.current]}
                className="w-full max-w-5xl mx-auto"
                onMouseEnter={plugin.current.stop}
                onMouseLeave={plugin.current.reset}
              >
                <CarouselContent>
                  {[
                    {
                      name: "David Garrido",
                      time: "hace 2 años",
                      text: "Llevo 10 años trabajando con el Abogado Salazar y nos ha ayudado en cualquier trámite que hemos realizado con su despacho. 100% Recomendado para tu trámite Migratorio.",
                      type: "Empresario"
                    },
                    {
                      name: "Gerardo C.",
                      time: "hace 4 años",
                      text: "Tuve el placer de trabajar con Aldo y esto resultó en una navegación exitosa de los requisitos de USCIS. Gran servicio considerando que el proceso se realizó completamente remoto (modo pandemia).",
                      type: "Inversionista"
                    },
                    {
                      name: "María Fernanda López",
                      time: "hace 6 meses",
                      text: "Como profesional de la salud, necesitaba una visa TN para trabajar en Estados Unidos. El equipo de InterLegalP me guió en cada paso del proceso. Obtuve mi visa en tiempo récord. ¡Altamente recomendados!",
                      type: "Profesional"
                    },
                    {
                      name: "Carlos Mendoza",
                      time: "hace 1 año",
                      text: "Invertí en un negocio en Texas y necesitaba la visa E-2. Los abogados de InterLegalP estructuraron todo perfectamente. Ahora mi empresa está operando exitosamente gracias a su asesoría estratégica.",
                      type: "Inversionista"
                    },
                    {
                      name: "Sofía Ramírez",
                      time: "hace 8 meses",
                      text: "Como estudiante de maestría, estaba perdida con el proceso de visa F-1. InterLegalP no solo me ayudó a obtener mi visa, sino que también me orientaron sobre mis opciones de trabajo post-graduación. Excelente servicio.",
                      type: "Estudiante"
                    },
                    {
                      name: "Roberto Jiménez",
                      time: "hace 3 meses",
                      text: "Busqué varios despachos antes de decidirme por InterLegalP. La diferencia fue notable: atención personalizada, respuestas rápidas y un enfoque realmente profesional. Mi visa EB-5 fue aprobada sin complicaciones.",
                      type: "Inversionista"
                    }
                  ].map((testimonio, index) => (
                    <CarouselItem key={index} className="md:basis-1/2 lg:basis-1/3">
                      <div className="p-2">
                        <Card className="testimonial-card h-full">
                          <CardContent className="p-6 flex flex-col h-full">
                            <div className="flex items-center justify-between mb-4">
                              <div>
                                <p className="font-bold text-gray-900">{testimonio.name}</p>
                                <p className="text-sm text-gray-500">{testimonio.time}</p>
                              </div>
                              <span className="text-xs bg-[#0a1628] text-white px-3 py-1 rounded-full">
                                {testimonio.type}
                              </span>
                            </div>
                            <div className="flex mb-4">
                              {[...Array(5)].map((_, i) => (
                                <span key={i} className="text-[#d4af37] text-xl">★</span>
                              ))}
                            </div>
                            <p className="text-gray-700 text-sm leading-relaxed flex-grow">
                              "{testimonio.text}"
                            </p>
                          </CardContent>
                        </Card>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious className="testimonial-arrow" />
                <CarouselNext className="testimonial-arrow" />
              </Carousel>
            </div>

            <div className="text-center">
              <Button onClick={scrollToContact} className="cta-button">
                Solicitar Consulta
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="section-dark">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-16">
              <div className="section-badge-light">FAQ</div>
              <h2 className="section-title-light">
                <span className="block">Dudas comunes sobre visas a</span>
                <span className="block mt-2">Estados Unidos</span>
              </h2>
            </div>
            
            <Accordion type="single" collapsible className="space-y-4">
              {[
                {
                  question: '¿Cómo sé qué tipo de visa es adecuada para mí?',
                  answer: 'Cada perfil es distinto. En la evaluación inicial analizamos su situación profesional, académica o de inversión para determinar la categoría más viable.'
                },
                {
                  question: '¿Pueden garantizar la aprobación de la visa?',
                  answer: 'Ningún despacho puede garantizar un resultado, ya que la decisión final depende de la autoridad migratoria. Nuestro enfoque es estructurar el expediente de forma sólida para maximizar la viabilidad.'
                },
                {
                  question: '¿Qué pasa si mi visa fue rechazada anteriormente?',
                  answer: 'Es necesario analizar el motivo del rechazo y determinar si es viable presentar una nueva solicitud con una estrategia distinta.'
                },
                {
                  question: '¿Atienden únicamente inversionistas?',
                  answer: 'No. Asesoramos inversionistas, profesionistas, estudiantes y solicitantes de visa de turismo que requieran orientación formal.'
                },
                {
                  question: '¿La consulta inicial tiene costo?',
                  answer: 'La evaluación preliminar permite determinar la viabilidad del caso y explicar los pasos a seguir.'
                }
              ].map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="faq-item">
                  <AccordionTrigger className="text-left text-white hover:text-[#d4af37] transition-colors">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-300">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="text-center mt-12">
              <Button onClick={scrollToContact} className="cta-button-light">
                Solicitar Consulta
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contacto" className="section-light">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <div className="section-badge-blue">Consulta</div>
              <h2 className="section-title">
                Inicie su proceso migratorio con asesoría estratégica
              </h2>
              <p className="section-subtitle mt-6">
                Tomar decisiones migratorias sin análisis previo puede afectar futuras solicitudes.
              </p>
              <p className="section-subtitle mt-2">
                En InterlegalP evaluamos su perfil y le orientamos con criterio jurídico antes de iniciar cualquier trámite.
              </p>
            </div>

            <div className="max-w-3xl mx-auto">
              <Card className="contact-card">
                <CardContent className="p-8">
                  <div className="ghl-form-container">
                    <iframe
                      src="https://links.thefunnelbox.com/widget/form/2tkt1GSIRcYRkwXO7Hry"
                      style={{width:'100%', height:'100%', border:'none', borderRadius:'3px'}}
                      id="inline-2tkt1GSIRcYRkwXO7Hry" 
                      data-layout="{'id':'INLINE'}"
                      data-trigger-type="alwaysShow"
                      data-trigger-value=""
                      data-activation-type="alwaysActivated"
                      data-activation-value=""
                      data-deactivation-type="neverDeactivate"
                      data-deactivation-value=""
                      data-form-name="FORM_GOOGLE_ADS_VISA_GENERAL"
                      data-height="undefined"
                      data-layout-iframe-id="inline-2tkt1GSIRcYRkwXO7Hry"
                      data-form-id="2tkt1GSIRcYRkwXO7Hry"
                      title="FORM_GOOGLE_ADS_VISA_GENERAL"
                    />
                  </div>
              </CardContent>
            </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0a1628] text-white py-8">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_google-ads-landing-1/artifacts/wfsf087n_image.png" 
              alt="InterlegalP" 
              className="h-16 w-auto mx-auto"
            />
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
